class LVVerifiedReviewsV57 extends HTMLElement {
  get isEnglish() { return this.dataset.locale === 'en'; }
  say(turkish, english) { return this.isEnglish ? english : turkish; }
  reviewTitle(review) { return this.isEnglish ? (review.title_en || review.title) : review.title; }
  reviewText(review) { return this.isEnglish ? (review.text_en || review.text) : review.text; }
  reviewResponse(review) { return this.isEnglish ? (review.merchant_response_en || review.merchant_response || review.merchantResponse) : (review.merchant_response || review.merchantResponse); }

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    const legacy = document.querySelector('#lv-rev')?.closest('accordion-custom');
    if (legacy) legacy.hidden = true;
    this.button = this.querySelector('[data-lv-review-load]');
    this.list = this.querySelector('[data-lv-review-list]');
    this.activeFilter = 'all';
    this.activeSort = 'newest';
    this.querySelector('[data-review-sort]')?.addEventListener('change', (event) => {
      this.activeSort = event.target.value;
      this.load();
    });
    this.querySelectorAll('[data-review-view]').forEach((button) => button.addEventListener('click', () => this.load('', button.dataset.reviewView)));
    this.filterButtons = [...this.querySelectorAll('[data-lv-review-filter]')];
    this.photoItems = [...this.querySelectorAll('[data-lv-review-gallery-photo]')].map((button) => ({
      src: button.dataset.src,
      reviewId: button.dataset.reviewId,
      name: button.dataset.reviewName,
      model: button.dataset.model,
      date: button.dataset.date
    }));
    this.reviews = this.readReviews();
    this.button?.addEventListener('click', () => this.load());
    this.filterButtons.forEach((button) => button.addEventListener('click', () => this.load('', button.dataset.lvReviewFilter)));
    this.addEventListener('click', (event) => this.handleClick(event));
    this.questionForm = this.querySelector('[data-lv-question-form]');
    this.questionForm?.addEventListener('submit', (event) => this.submitQuestion(event));
    this.prepareExpandableContent(this);
  }

  handleClick(event) {
    const galleryButton = event.target.closest('[data-lv-review-gallery-photo]');
    if (galleryButton) {
      const index = [...this.querySelectorAll('[data-lv-review-gallery-photo]')].indexOf(galleryButton);
      this.openMedia(index);
      return;
    }
    const cardPhoto = event.target.closest('[data-lv-review-open-photo]');
    if (cardPhoto) {
      const matches = this.photoItems.map((item, index) => ({ item, index })).filter(({ item }) => item.reviewId === cardPhoto.dataset.lvReviewOpenPhoto);
      const offset = Number(cardPhoto.dataset.photoIndex) || 0;
      if (matches.length) this.openMedia(matches[Math.min(offset, matches.length - 1)].index);
      else if (cardPhoto.dataset.src) {
        const index = this.photoItems.push({
          src: cardPhoto.dataset.src,
          reviewId: cardPhoto.dataset.lvReviewOpenPhoto,
          name: cardPhoto.dataset.reviewName,
          model: cardPhoto.dataset.model,
          date: cardPhoto.dataset.date
        }) - 1;
        this.openMedia(index);
      }
      return;
    }
    const toggle = event.target.closest('[data-lv-review-toggle]');
    if (toggle) this.toggleExpandable(toggle);
  }

  readReviews() {
    try {
      const liveReviews = JSON.parse(this.querySelector('[data-lv-review-data]')?.textContent || '[]');
      const staticReviews = JSON.parse(this.querySelector('[data-lv-review-static-data]')?.textContent || '[]');
      const live = Array.isArray(liveReviews) ? liveReviews : [];
      const fallback = Array.isArray(staticReviews) ? staticReviews : [];
      const manual = this.isEnglish ? JSON.parse(this.querySelector('[data-lv-review-manual-translations]')?.textContent || '{}') : {};
      const reviews = this.querySelector('[data-lv-review-data]') ? live : fallback;
      return this.sortReviewsForDecision(reviews.map((review) => {
        const translation = manual[String(review.id)] || {};
        return {
          ...review,
          title_en: review.title_en || translation.title_en || null,
          text_en: review.text_en || translation.text_en || null,
          merchant_response_en: review.merchant_response_en || translation.merchant_response_en || null,
          manuallyTranslated: !review.text_en && Boolean(translation.text_en)
        };
      }));
    } catch (_error) { return []; }
  }

  sortReviewsForDecision(reviews) {
    return reviews.map((review, index) => ({ review, index, date: Date.parse(review.date) }))
      .sort((a, b) => {
        if (!Number.isFinite(a.date)) return Number.isFinite(b.date) ? 1 : a.index - b.index;
        if (!Number.isFinite(b.date)) return -1;
        return (this.activeSort === 'newest' ? b.date - a.date : a.date - b.date) || a.index - b.index;
      }).map(({ review }) => review);
  }

  load(reviewId = '', filter = this.activeFilter) {
    if (this.loading) return;
    this.loading = true;
    if (this.button) {
      this.button.disabled = true;
      this.button.textContent = this.say('Yorumlar hazırlanıyor…', 'Preparing reviews…');
    }
    const reviews = this.readReviews();
    this.reviews = reviews;
    this.activeFilter = filter;
    this.querySelectorAll('[data-review-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.reviewView === filter)));
    this.filterButtons.forEach((filterButton) => filterButton.setAttribute('aria-pressed', String(filterButton.dataset.lvReviewFilter === filter)));
    this.render(reviews, filter);
    if (this.button) {
      this.button.hidden = reviews.length > 0;
      this.button.disabled = false;
    }
    this.loading = false;
    if (reviewId) requestAnimationFrame(() => {
      const target = this.list?.querySelector(`[data-review-id="${CSS.escape(reviewId)}"]`);
      target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
    });
  }

  announce(message) {
    const status = this.querySelector('[data-lv-review-status]');
    if (status) status.textContent = message;
  }

  formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return '';
    return new Intl.DateTimeFormat(this.isEnglish ? 'en-US' : 'tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  cleanReviewText(value) {
    const text = String(value || '').trim();
    const lines = text.split(/\r?\n/);
    const internalHeader = /(?:old_shopify_order|verified_purchase|customer_experience|shopify_order)/.test(lines[0] || '');
    return (internalHeader ? lines.slice(1).join('\n').trim() : text)
      .replace(/\b(?:old_shopify_order|verified_purchase|customer_experience|shopify_order)\b/g, '')
      .replace(/\s*·\s*·\s*/g, ' · ').trim();
  }

  render(reviews, filter = 'all') {
    this.list.replaceChildren();
    if (!reviews.length) { this.announce(this.say('Henüz yayımlanmış yorum yok.', 'There are no published reviews yet.')); return; }
    const filtered = filter === 'photo'
      ? reviews.filter((review) => Array.isArray(review.media) && review.media.length)
      : reviews;
    if (!filtered.length) {
      const empty = document.createElement('p'); empty.className = 'lv-review-v57__empty'; empty.textContent = this.say('Bu filtrede henüz yayımlanmış deneyim yok.', 'There are no experiences in this filter yet.'); this.list.append(empty);
    }
    filtered.forEach((review) => this.list.append(this.card(review)));
    this.announce(this.say(`${filtered.length} yorum gösteriliyor.`, `${filtered.length} reviews shown.`));
    this.prepareExpandableContent(this.list);
  }

  card(review) {
    const article = document.createElement('article');
    article.className = 'lv-review-v57__card';
    article.dataset.reviewId = review.id;
    article.tabIndex = -1;
    const top = document.createElement('div'); top.className = 'lv-review-v57__card-top';
    const identity = document.createElement('div');
    const name = document.createElement('div'); name.className = 'lv-review-v57__name'; name.textContent = review.name || this.say('VeloraWork müşterisi', 'VeloraWork customer'); identity.append(name);
    if (review.badge !== 'no_badge') {
      const badge = document.createElement('span'); badge.className = `lv-review-v57__badge${review.badge === 'customer_experience' ? ' lv-review-v57__badge--experience' : ''}`;
      badge.textContent = review.badge === 'verified_purchase' ? this.say('✓ Doğrulanmış alışveriş', '✓ Verified purchase') : this.say('Kayıtlı müşteri deneyimi', 'Recorded customer experience'); identity.append(badge);
    }
    const rating = document.createElement('div'); rating.className = 'lv-review-v57__rating';
    const stars = document.createElement('div'); stars.className = 'lv-review-v57__stars';
    const ratingValue = Math.max(1, Math.min(5, Number(review.rating) || 1));
    stars.setAttribute('aria-label', this.say(`5 üzerinden ${ratingValue} puan`, `${ratingValue} out of 5`)); stars.textContent = '★'.repeat(ratingValue) + '☆'.repeat(5 - ratingValue); rating.append(stars);
    const formattedDate = this.formatDate(review.date);
    if (formattedDate) { const time = document.createElement('time'); time.className = 'lv-review-v57__date'; time.dateTime = review.date; time.textContent = formattedDate; rating.append(time); }
    top.append(identity, rating); article.append(top);
    const reviewTitle = this.reviewTitle(review);
    if (reviewTitle) { const title = document.createElement('h4'); title.className = 'lv-review-v57__title'; title.textContent = reviewTitle; article.append(title); }
    const text = document.createElement('p'); text.className = 'lv-review-v57__text'; text.id = `lv-review-v57-dialog-text-${review.id}`; text.dataset.lvReviewExpandable = ''; text.textContent = this.cleanReviewText(this.reviewText(review)); article.append(text, this.expandButton(text, this.say('Yorumun tamamını oku', 'Read full review'), this.say('Yorumu kısalt', 'Show less')));
    if (this.isEnglish && review.text_en) { const note = document.createElement('p'); note.className = 'lv-review-v57__translation-note'; note.textContent = review.manuallyTranslated ? 'Customer in Türkiye · Translated from Turkish' : 'Customer in Türkiye · Automatically translated from Turkish'; article.append(note); }
    const context = this.contextBadges(review);
    if (context) article.append(context);
    if (Array.isArray(review.media) && review.media.length) article.append(this.mediaRow(review));
    const merchantResponse = this.reviewResponse(review);
    if (merchantResponse) {
      const reply = document.createElement('div'); reply.className = 'lv-review-v57__response';
      const head = document.createElement('div');
      const label = document.createElement('strong'); label.textContent = this.say('VeloraWork yanıtı', 'VeloraWork reply'); head.append(label);
      const merchantResponseDate = review.merchant_response_date || review.merchantResponseDate;
      if (merchantResponseDate) { const time = document.createElement('time'); time.dateTime = merchantResponseDate; time.textContent = this.formatDate(merchantResponseDate); head.append(time); }
      const copy = document.createElement('p'); copy.id = `lv-review-v57-dialog-response-${review.id}`; copy.dataset.lvReviewExpandable = ''; copy.textContent = merchantResponse;
      reply.append(head, copy, this.expandButton(copy, this.say('VeloraWork yanıtının tamamını oku', 'Read full VeloraWork reply'), this.say('Yanıtı kısalt', 'Show less'))); article.append(reply);
    }
    return article;
  }

  contextBadges(review) {
    const labels = this.isEnglish ? {
      usage_area: { office: 'Used at the office', cafe: 'Used at cafés', school: 'Used at school', design_studio: 'Used in a design studio', home: 'Used at home', other: 'Used in varied settings' },
      usage_duration: { first_impression: 'First impression', one_month: 'Used for 1 month', three_months: 'Used for 3 months', six_months_plus: 'Used for 6+ months' },
      laptop_size: { '13_14': '13–14″ laptop', '15_15_6': '15–15.6″ laptop', '16': '16″ laptop', no_laptop: 'No laptop carried' }
    } : {
      usage_area: { office: 'Ofiste kullanıyor', cafe: 'Kafede kullanıyor', school: 'Okulda kullanıyor', design_studio: 'Tasarım stüdyosunda', home: 'Evde kullanıyor', other: 'Farklı ortamlarda' },
      usage_duration: { first_impression: 'İlk izlenim', one_month: '1 aydır kullanıyor', three_months: '3 aydır kullanıyor', six_months_plus: '6+ aydır kullanıyor' },
      laptop_size: { '13_14': '13–14″ laptop', '15_15_6': '15–15,6″ laptop', '16': '16″ laptop', no_laptop: 'Laptop taşımıyor' }
    };
    const values = [labels.usage_area[review.usage_area], labels.usage_duration[review.usage_duration], labels.laptop_size[review.laptop_size]].filter(Boolean);
    if (!values.length) return null;
    const row = document.createElement('div'); row.className = 'lv-review-context-v67'; row.setAttribute('aria-label', this.say('Müşterinin kullanım bilgileri', 'Customer usage details'));
    values.forEach((value) => { const badge = document.createElement('span'); badge.textContent = value; row.append(badge); });
    return row;
  }

  mediaRow(review) {
    const media = document.createElement('div'); media.className = 'lv-review-v57__media';
    review.media.forEach((item, index) => {
      const previewSrc = typeof item === 'string' ? item : (item.preview_url || item.url);
      const fullSrc = typeof item === 'string' ? item : (item.url || item.preview_url);
      if (!previewSrc || !fullSrc) return;
      let mediaIndex = this.photoItems.findIndex((photo) => photo.src === fullSrc);
      if (mediaIndex < 0) mediaIndex = this.photoItems.push({ src: fullSrc, reviewId: review.id, name: review.name, model: this.dataset.model || '', date: review.date }) - 1;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'lv-review-v57__media-button'; button.setAttribute('aria-label', this.say(`Müşteri fotoğrafı ${index + 1} büyük görüntüle`, `Open customer photo ${index + 1}`)); button.addEventListener('click', () => this.openMedia(mediaIndex));
      const img = document.createElement('img'); img.src = previewSrc; img.alt = this.say(`Müşteri ürün fotoğrafı ${index + 1}`, `Customer product photo ${index + 1}`); img.loading = 'lazy'; img.decoding = 'async'; img.width = 96; img.height = 96; button.append(img); media.append(button);
    });
    return media;
  }

  ensureDialog() {
    if (this.dialog) return;
    const dialog = document.createElement('dialog'); dialog.className = 'lv-review-v57__dialog';
    const close = document.createElement('button'); close.type = 'button'; close.className = 'lv-review-v57__dialog-close'; close.setAttribute('aria-label', this.say('Fotoğrafı kapat', 'Close photo')); close.textContent = '×'; close.addEventListener('click', () => dialog.close());
    const previous = document.createElement('button'); previous.type = 'button'; previous.className = 'lv-review-v57__dialog-nav lv-review-v57__dialog-nav--prev'; previous.setAttribute('aria-label', this.say('Önceki müşteri fotoğrafı', 'Previous customer photo')); previous.textContent = '‹'; previous.addEventListener('click', () => this.showMedia(this.mediaIndex - 1));
    const figure = document.createElement('figure');
    const image = document.createElement('img'); image.className = 'lv-review-v57__dialog-image';
    const caption = document.createElement('figcaption'); caption.className = 'lv-review-v57__dialog-caption'; figure.append(image, caption);
    const next = document.createElement('button'); next.type = 'button'; next.className = 'lv-review-v57__dialog-nav lv-review-v57__dialog-nav--next'; next.setAttribute('aria-label', this.say('Sonraki müşteri fotoğrafı', 'Next customer photo')); next.textContent = '›'; next.addEventListener('click', () => this.showMedia(this.mediaIndex + 1));
    dialog.append(close, previous, figure, next);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('keydown', (event) => { if (event.key === 'ArrowLeft') this.showMedia(this.mediaIndex - 1); if (event.key === 'ArrowRight') this.showMedia(this.mediaIndex + 1); });
    this.append(dialog); this.dialog = dialog; this.dialogImage = image; this.dialogCaption = caption; this.dialogPrevious = previous; this.dialogNext = next;
  }

  openMedia(index) { if (!this.photoItems.length) return; this.ensureDialog(); this.showMedia(index); this.dialog.showModal(); }

  showMedia(index) {
    if (!this.photoItems.length) return;
    this.mediaIndex = (index + this.photoItems.length) % this.photoItems.length;
    const item = this.photoItems[this.mediaIndex];
    this.dialogImage.src = item.src; this.dialogImage.alt = this.say(`${item.model || 'VeloraWork'} müşteri fotoğrafı`, `${item.model || 'VeloraWork'} customer photo`);
    const review = this.findReview(item.reviewId);
    const meta = document.createElement('div'); meta.className = 'lv-review-v57__dialog-meta';
    const name = document.createElement('strong'); name.textContent = item.name || review?.name || this.say('VeloraWork müşterisi', 'VeloraWork customer');
    const details = document.createElement('span'); details.textContent = [item.model || this.dataset.model, item.date ? this.formatDate(item.date) : this.formatDate(review?.date)].filter(Boolean).join(' · ');
    meta.append(name, details);
    const quote = document.createElement('p'); quote.textContent = this.cleanReviewText(review ? this.reviewText(review) : '');
    this.dialogCaption.replaceChildren(meta);
    if (quote.textContent) this.dialogCaption.append(quote);
    const multiple = this.photoItems.length > 1; this.dialogPrevious.hidden = !multiple; this.dialogNext.hidden = !multiple;
  }

  expandButton(target, expandLabel, collapseLabel) {
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'lv-review-v57__read'; button.dataset.lvReviewToggle = '';
    button.setAttribute('aria-controls', target.id); button.setAttribute('aria-expanded', 'false');
    button.dataset.expandLabel = expandLabel; button.dataset.collapseLabel = collapseLabel;
    button.textContent = expandLabel; button.hidden = true;
    return button;
  }

  prepareExpandableContent(scope) {
    requestAnimationFrame(() => {
      scope?.querySelectorAll('[data-lv-review-toggle]').forEach((button) => {
        const target = document.getElementById(button.getAttribute('aria-controls'));
        if (!target) return;
        button.hidden = target.scrollHeight <= target.clientHeight + 2;
      });
    });
  }

  toggleExpandable(button) {
    const target = document.getElementById(button.getAttribute('aria-controls'));
    if (!target) return;
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    target.classList.toggle('is-expanded', expanded);
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
  }

  findReview(reviewId) {
    return (this.reviews || []).find((review) => String(review.id) === String(reviewId));
  }

  async submitQuestion(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('button[type="submit"]');
    const status = form.querySelector('[data-lv-question-status]');
    button.disabled = true;
    status.textContent = this.say('Sorunuz gönderiliyor…', 'Sending your question…');
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form)))
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || this.say('Sorunuz gönderilemedi.', 'Your question could not be sent.'));
      form.reset();
      status.textContent = this.say(data.message || 'Sorunuz alındı; yanıtlanmadan önce kontrol edilecek.', 'We received your question. It will be reviewed before publication.');
    } catch (error) {
      status.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('lv-verified-reviews')) customElements.define('lv-verified-reviews', LVVerifiedReviewsV57);
